import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { Customer } from 'src/app/core/models/customer.model';
import { CustomerStoreService } from 'src/app/core/services/customer-store.service';
import { PhoneNumberEditForm } from 'src/app/shared/forms/phone-number-edit.form';
import { PhoneNumberVerificationCodeForm } from 'src/app/shared/forms/phone-number-verification-code.form';
import { LoadingAction } from 'src/app/shared/models/loading-action.enum';
import { PhoneNumberEditStep } from 'src/app/shared/models/phone-number-edit-step.enum';
import { PhoneNumberUsageStatus } from 'src/app/shared/models/phone-number-usage-status.enum';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { PhoneNumberVerificationApiService } from 'src/app/shared/services/phone-number-verification-api.service';
import { Timer } from 'src/app/shared/utils/timer.utils';

export const PHONE_VERIFIED_EVENT = 'PHONE_NUMBER_VERIFIED';

@Component({
standalone: false,
  selector: 'app-phone-number-edit-dialog',
  styleUrls: ['./phone-number-edit-dialog.component.scss'],
  templateUrl: './phone-number-edit-dialog.component.html',
})
export class PhoneNumberEditDialogComponent implements OnInit, OnDestroy {
  public phoneNumberEditForm = new PhoneNumberEditForm();
  public phoneNumberVerificationCodeForm = new PhoneNumberVerificationCodeForm();
  public step: PhoneNumberEditStep = PhoneNumberEditStep.PHONE_NUMBER;
  public timer$: Observable<number>;
  public unsubscribe = new Subject();
  public customer$: Observable<Customer>;

  public phoneNumberEditStep = PhoneNumberEditStep;
  public timerValue: number;
  public loadingAction = LoadingAction;
  public phoneVerified = false;

  constructor(
    private dialogRef: MatDialogRef<PhoneNumberEditDialogComponent>,
    public customerStoreService: CustomerStoreService,
    private snackBar: MatSnackBar,
    private phoneNumberVerificationApiService: PhoneNumberVerificationApiService,
    public loadingService: LoadingService,
  ) {}

  public ngOnInit(): void {
    this.customer$ = this.customerStoreService.state$.pipe(
      filter((customer) => !!customer),
      tap((customer) => this.phoneNumberEditForm.phoneNumber.setValue(customer.phoneNumber)),
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
  }

  public saveNumber(): void {
    const { verificationStatus, phoneNumber } = this.customerStoreService.state;

    if (verificationStatus === 'PHONE_VERIFIED' && phoneNumber === this.phoneNumberEditForm.phoneNumber.value) {
      this.dialogRef.close(true);
      return;
    }

    this.hasUsedCoupon().subscribe((data) => {
      if (data) {
        this.step = PhoneNumberEditStep.PHONE_NUMBER_USED_WITH_COUPON;
      } else {
        this.sendVerificationSMS();
      }
    });
  }

  public acceptCouponLost(): void {
    this.sendVerificationSMS();
  }

  public save(): void {
    const verificationCodeControl = this.phoneNumberVerificationCodeForm.verificationCode;

    if (!verificationCodeControl.value || verificationCodeControl.invalid) {
      return;
    }

    this.customerStoreService
      .verifyPhoneNumber({
        verificationCode: this.phoneNumberVerificationCodeForm.verificationCode.value,
      })
      .pipe(
        tap(() => {
          this.phoneVerified = true;
          this.dialogRef.close(true);
          // this.snackBar.open('Numer telefonu został potwierdzony', null, { duration: DURATION_5S });
        }),
        catchError(async (e) => {
          if (e.error && e.error.errorCodes && e.error.errorCodes.includes('INVALID_PHONE_NUMBER_VERIFICATION_CODE')) {
            this.setErrorState(verificationCodeControl, 'invalidCode');
          }
        }),
      )
      .subscribe();
  }

  public getPhoneNumberErrorMessage(errors: ValidationErrors): string {
    if (errors.tooManyRequests) {
      return 'Przekroczono liczbę prób. Spróbuj ponownie za 1 minutę';
    } else if (errors.phoneNumberNotUnique) {
      return 'Podany numer jest już zajęty';
    } else {
      return 'Podaj poprawny numer telefonu';
    }
  }

  public sendVerificationSMS(): void {
    if (this.timerValue > 0) {
      return;
    }

    this.phoneNumberVerificationApiService
      .phoneNumberVerification(this.phoneNumberEditForm.phoneNumber.value)
      .pipe(
        tap(() => {
          this.step = PhoneNumberEditStep.VERIFICATION_CODE;
          this.setVerificationTimer();

          // this.snackBar.open('Wysłano SMS z kodem weryfikacyjnym', null, { duration: DURATION_5S });
        }),
        catchError(async (e) => {
          if (e.status && e.status === 429) {
            this.setErrorState(this.phoneNumberEditForm.phoneNumber, 'tooManyRequests');
          }
        }),
      )
      .subscribe();
  }

  public close(completed: boolean) {
    this.dialogRef.close(completed);
  }

  private hasUsedCoupon(): Observable<boolean> {
    return this.phoneNumberVerificationApiService.phoneNumberUsage(this.phoneNumberEditForm.phoneNumber.value).pipe(
      filter((resp) => {
        if (resp === 'USED_WITH_ACTIVE_USER') {
          this.setErrorState(this.phoneNumberEditForm.phoneNumber, 'phoneNumberNotUnique');

          return false;
        }

        return true;
      }),
      map((data) => data === PhoneNumberUsageStatus.USED_WITH_COUPON),
    );
  }

  private setVerificationTimer(): void {
    const timer = new Timer('resend_sms');

    this.timer$ = timer.initTimer().pipe(tap((t) => (this.timerValue = t)));
  }

  private setErrorState(formControl: FormControl, key: string): void {
    formControl.setErrors({
      [key]: true,
    });
    formControl.markAsTouched();
  }
}
